import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import Footer from '../components/Footer';
import useAuthModal from '../hooks/useAuthModal';
import { getCareerBySlug } from '../config/careerPages';

export default function CareerLandingScreen() {
  const navigate = useNavigate();
  const { careerSlug } = useParams();
  const authModal = useAuthModal();
  const [openFaq, setOpenFaq] = useState(null);

  const career = useMemo(() => getCareerBySlug(careerSlug), [careerSlug]);

  if (!career) {
    return (
      <div className="min-h-screen bg-[#F6F7FB] text-[#1E293B]">
        <SiteHeader authModal={authModal} />
        <main className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h1 className="text-3xl font-semibold">Career page not found</h1>
          <p className="text-gray-500 mt-3">Please choose another role or use regular search.</p>
          <button
            type="button"
            onClick={() => navigate('/search')}
            className="btn-orange mt-6 px-6 py-3 text-sm rounded-2xl"
          >
            Go to search
          </button>
        </main>
        <Footer />
      </div>
    );
  }

  const { title, headline, description, questions, competencies, faq } = career;

  const toggleFaq = (index) => setOpenFaq(openFaq === index ? null : index);

  return (
    <div className="min-h-screen bg-[#F6F7FB] text-[#1E293B]">
      <SiteHeader authModal={authModal} />

      <main className="max-w-5xl mx-auto px-6 py-12">

        {/* ── HERO ── */}
        <section className="bg-white rounded-[32px] shadow-sm p-8 md:p-12 text-center mb-8">
          <span className="inline-block bg-orange-50 text-orange-600 text-xs font-semibold px-3 py-1 rounded-full mb-4 uppercase tracking-wide">
            AI Interview Prep
          </span>
          <h1 className="text-4xl md:text-5xl font-semibold leading-tight">
            {headline || `AI Interview Prep for ${title}`}
          </h1>
          <p className="text-gray-500 mt-4 max-w-2xl mx-auto text-lg">
            {description || `Practice mock interviews for ${title} positions. Get instant AI feedback and find top jobs.`}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={() => navigate(`/search?q=${encodeURIComponent(title)}`)}
              className="btn-orange px-6 py-3 text-sm rounded-2xl"
            >
              Start interview practice →
            </button>
            <button
              type="button"
              onClick={() => navigate('/pricing')}
              className="px-6 py-3 text-sm rounded-2xl border border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              View pricing
            </button>
          </div>

          <p className="text-xs text-gray-400 mt-4">3 free sessions · No credit card · Results in under 5 minutes</p>
        </section>

        {/* ── COMPETENCIES ── */}
        {competencies && competencies.length > 0 && (
          <section className="bg-white rounded-[24px] shadow-sm p-8 mb-8">
            <h2 className="text-xl font-semibold mb-4">What {title} Interviews Test</h2>
            <div className="flex flex-wrap gap-2">
              {competencies.map((comp) => (
                <span
                  key={comp}
                  className="bg-orange-50 text-orange-700 text-sm font-medium px-4 py-2 rounded-full border border-orange-100"
                >
                  {comp}
                </span>
              ))}
            </div>
            <p className="text-gray-500 text-sm mt-4">
              JobSkills generates questions across all these areas — tailored to the exact job description you paste.
            </p>
          </section>
        )}

        {/* ── SAMPLE QUESTIONS ── */}
        {questions && questions.length > 0 && (
          <section className="bg-white rounded-[24px] shadow-sm p-8 mb-8">
            <h2 className="text-xl font-semibold mb-2">
              Real {title} Interview Questions to Practice
            </h2>
            <p className="text-gray-500 text-sm mb-6">
              These are the types of questions you will be asked. Practice them with AI until your answers are clear and structured.
            </p>
            <ol className="space-y-3">
              {questions.map((q, i) => (
                <li key={i} className="flex gap-3 items-start">
                  <span className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 text-orange-600 text-xs font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <span className="text-gray-700 text-sm leading-relaxed">{q}</span>
                </li>
              ))}
            </ol>
            <button
              type="button"
              onClick={() => navigate(`/search?q=${encodeURIComponent(title)}`)}
              className="mt-6 btn-orange px-5 py-2.5 text-sm rounded-xl"
            >
              Practice these questions with AI →
            </button>
          </section>
        )}

        {/* ── HOW IT WORKS ── */}
        <section className="bg-white rounded-[24px] shadow-sm p-8 mb-8">
          <h2 className="text-xl font-semibold mb-6">How AI Interview Practice Works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { step: '01', title: 'Paste the job description', text: `Find a ${title} role you want. Paste the job posting — the AI reads it and generates questions for that exact position.` },
              { step: '02', title: 'Answer like in a real interview', text: 'Type your answers. The AI asks follow-ups, keeps you on track, and listens for structure and completeness.' },
              { step: '03', title: 'Get BARS-scored feedback', text: 'Each answer is scored using the Behaviorally Anchored Rating Scale — the same framework recruiters at top companies use.' },
            ].map(({ step, title: stepTitle, text }) => (
              <div key={step} className="flex flex-col gap-2">
                <span className="text-3xl font-bold text-orange-200">{step}</span>
                <h3 className="font-semibold text-gray-800">{stepTitle}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── FAQ ── */}
        {faq && faq.length > 0 && (
          <section className="bg-white rounded-[24px] shadow-sm p-8 mb-8">
            <h2 className="text-xl font-semibold mb-6">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {faq.map((item, i) => (
                <div key={i} className="border border-gray-100 rounded-2xl overflow-hidden">
                  <button
                    type="button"
                    className="w-full flex justify-between items-center text-left px-5 py-4 text-sm font-medium text-gray-800 hover:bg-gray-50 transition-colors"
                    onClick={() => toggleFaq(i)}
                  >
                    <span>{item.q}</span>
                    <svg
                      className={`flex-shrink-0 ml-4 w-4 h-4 text-gray-400 transition-transform ${openFaq === i ? 'rotate-45' : ''}`}
                      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    >
                      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4 text-sm text-gray-500 leading-relaxed border-t border-gray-50">
                      {item.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── FINAL CTA ── */}
        <section className="bg-[#1E293B] rounded-[24px] p-8 md:p-12 text-center text-white">
          <h2 className="text-2xl md:text-3xl font-semibold">
            Ready to practice your {title} interview?
          </h2>
          <p className="text-gray-400 mt-3 max-w-xl mx-auto text-sm">
            Start with 3 free sessions. Get real BARS feedback. Walk into your next interview prepared.
          </p>
          <button
            type="button"
            onClick={() => navigate(`/search?q=${encodeURIComponent(title)}`)}
            className="mt-6 bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-2xl text-sm font-semibold transition-colors"
          >
            Start free practice →
          </button>
          <p className="text-xs text-gray-500 mt-3">No credit card · 3 free sessions · Cancel anytime</p>
        </section>

      </main>

      <Footer />
    </div>
  );
}
